import React, { useMemo, useRef, useState } from 'react';
import { ColDef, ColumnState, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import {
  aggridHeaderClass,
  pdaCellStyle,
} from 'src/components/common/common';
import usePDA1080_R01Store from "./usePDA1080_R01Store";
import { usePDA1080_R01Content } from "./PDA1080_R01Content";
import { PDA1080R01GridData } from "./PDA1080_R01Dto";

type PDA1080R01GridDataWithInserted = PDA1080R01GridData & { inserted?: boolean };

/**
 * PDA1080_R01 반입 대기 목록 테이블
 * PDA1040_R01 디자인 스타일 적용 (ag-grid)
 */
const PDA1080_R01Table = () => {
  const { returnItems, loading } = usePDA1080_R01Store();
  const { handleSaveAll, handleCancel } = usePDA1080_R01Content();

  const gridRef = useRef<AgGridReact<PDA1080R01GridDataWithInserted>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  const paddedGridData = useMemo(() => {
    const data: PDA1080R01GridDataWithInserted[] = [...returnItems];
    return data;
  }, [returnItems]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: '품명',
        field: 'itemNm',
        valueGetter: (params) => {
          const itemNm = params.data?.itemNm || '-';
          const itemSpec = params.data?.itemSpec;
          return itemSpec ? `${itemNm}\n${itemSpec}` : itemNm;
        },
        cellStyle: { 
          textAlign: 'left', 
          ...pdaCellStyle,
          whiteSpace: 'pre-line',
        },
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 90,
      },
      {
        headerName: '위치',
        field: 'locationCd',
        valueGetter: (params) => params.data?.locationCd || '-',
        cellStyle: { 
          textAlign: 'left', 
          ...pdaCellStyle,
        },
        headerClass: aggridHeaderClass,
        flex: 1.5,
        minWidth: 90,
      },
      {
        headerName: '수량',
        field: 'returnQty',
        valueGetter: (params) => params.data?.returnQty ?? 0,
        valueFormatter: (params: ValueFormatterParams<PDA1080R01GridDataWithInserted>) =>
          params.value != null ? Number(params.value).toLocaleString() : '-',
        cellStyle: { 
          textAlign: 'right', 
          ...pdaCellStyle,
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 70,
      },
      {
        headerName: 'Lot',
        field: 'lotNo',
        valueGetter: (params) => params.data?.lotNo || '-',
        cellStyle: { 
          textAlign: 'left', 
          ...pdaCellStyle,
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 90,
      },
    ] as ColDef<PDA1080R01GridDataWithInserted>[],
    []
  );

  const initSelected = () => {};

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
    <div className="px-2 py-2  bg-white">
        <div className="flex gap-2">
          <button
            onClick={handleCancel}
            disabled={loading || returnItems.length === 0}
            className="flex-1 bg-gray-500 text-white px-3 py-1.5 rounded text-lg font-medium hover:bg-gray-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            검수 취소
          </button>
          <button
            onClick={handleSaveAll}
            disabled={loading || returnItems.length === 0}
            className="flex-1 bg-green-500 text-white px-3 py-1.5 rounded text-lg font-medium hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            등록
          </button>
        </div>
      </div>

      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1080_R01_Table_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<PDA1080R01GridDataWithInserted>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1080_R01_Table_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>

      
    </div>
  );
};

export default PDA1080_R01Table;
